import { Box, Link, Typography } from '@mui/material'
import { Divider, IconButton, TextLink } from 'utoniq-ui'

import { BaseLayout, Head } from '@/components/layouts'
import { LoadingWithBackdrop } from '@/components/organisms'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { getPlatform } from '@/core/native/app'

import { SignUpForm, SignUpFormProps } from './SignUpForm'

export * from './fragments'

export type State = {
  verifyEmailSending: boolean
  loading: boolean
}

export type Action = {
  onSubmitSignUp: SignUpFormProps['onSubmitSignUp']
  onSignUpWithGoogle: () => Promise<void>
  onSignUpWithApple: () => Promise<void>
}

export const SignUpTemplate: Template<State, Action> = ({
  state,
  onSignUpWithGoogle,
  onSignUpWithApple,
  onSubmitSignUp,
}) => {
  const { t } = useTranslation('signUp')
  const { routerNames, routerPages } = useRouter()

  return (
    <>
      <BaseLayout
        margin
        header={{
          title: t('title'),
          back: true,
          right: (
            <Link
              variant="subtitle1"
              color="text.secondary"
              href={
                config.modules.tutorial
                  ? routerPages.Tutorial
                  : routerPages.GlobalHome
              }
              sx={{ minWidth: 52 }}
            >
              {t('skip')}
            </Link>
          ),
        }}
      >
        <Head title={routerNames.SignUp} />
        {
          /**
           * @description
           * Diica以外の場合、SNS認証機能解放
           * （DiicaはSNS連携時のUtoniqアイコンの表示をしてはいけないため、SNSリンク機能を隠す）
           */
          config.applicationKey !== 'diica' && getPlatform() !== 'android' ? (
            <>
              <SocialLoginCell
                onSignUpWithGoogle={onSignUpWithGoogle}
                onSignUpWithApple={onSignUpWithApple}
              />
              <SupplementTextCell />
            </>
          ) : (
            <Box sx={{ py: 3 }} />
          )
        }
        <SignUpForm onSubmitSignUp={onSubmitSignUp} />
        {
          /**
           * @description
           * utoniq・digireaのみアーティスト登録機能へのリンク表示
           */
          ['utoniq', 'digirea'].some(key => key === config.applicationKey) && (
            <TextLinkCell />
          )
        }
      </BaseLayout>
      <LoadingWithBackdrop open={state.loading} />
    </>
  )
}

const SocialLoginCell: Cell<
  Pick<Action, 'onSignUpWithGoogle' | 'onSignUpWithApple'>
> = ({ onSignUpWithGoogle, onSignUpWithApple }) => {
  return (
    <CellFrame
      sx={{
        display: 'flex',
        justifyContent: 'center',
        py: 2,
        gap: 5,
      }}
    >
      <IconButton.Google
        size="large"
        onClick={onSignUpWithGoogle}
        bgcolor={config.theme.dark ? 'white' : 'grey.50'}
        /**
         * @todo
         * Googleログイン実装時に表示する
         * https://app.clickup.com/t/908127/UT-2827
         */
        sx={{ display: 'none' }}
      />
      {getPlatform() !== 'android' && (
        <IconButton.Apple
          size="large"
          onClick={onSignUpWithApple}
          bgcolor={config.theme.dark ? 'white' : '#221f20'}
          isDark={config.theme.dark}
        />
      )}
    </CellFrame>
  )
}

const SupplementTextCell: Cell = () => {
  const { t } = useTranslation('signUp')

  return (
    <CellFrame
      sx={{
        py: 3,
        textAlign: 'center',
      }}
    >
      <Divider>
        <Typography
          variant="caption"
          color="text.primary"
          sx={{ mx: 3, whiteSpace: 'nowrap' }}
        >
          {t('or')}
        </Typography>
      </Divider>
    </CellFrame>
  )
}

export const TextLinkCell: Cell = () => {
  const { t } = useTranslation('signUp')
  const { routerPages } = useRouter()

  return (
    <CellFrame display="flex" sx={{ mt: 2, py: 4 }} justifyContent="center">
      <TextLink
        underline="none"
        href={routerPages.SignUpArtist}
        variant="subtitle1"
      >
        {t('textToRegisterArtist')}
      </TextLink>
    </CellFrame>
  )
}
